import DynamicsIcon from '../common/DynamicsIcon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen, CalendarDays, ChevronRight, Code2, FileClock, FileText,
  GitCommitHorizontal, ListTree, RefreshCw, Search, ShieldCheck,
} from 'lucide-react';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';
import { useDynamicsPage, openDynamics } from '../../hooks/useDynamicsPage';
import { DYNAMICS_ENABLED } from '../../config/features';

const formatDate = value => value
  ? new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
  : 'Date indisponible';

const normalize = value => String(value || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const functionalSectionId = (groupIndex, pageIndex) => `documentation-functional-${groupIndex}-${pageIndex}`;
const technicalSectionId = key => `documentation-technical-${key}`;

function sanitizeHtml(html = '') {
  if (typeof window === 'undefined') return '';
  const document = new DOMParser().parseFromString(html, 'text/html');
  document.querySelectorAll('script, style, iframe, object, embed, link, meta, form').forEach(node => node.remove());
  document.querySelectorAll('*').forEach(node => {
    [...node.attributes].forEach(attribute => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      const protocol = value.includes(':') ? value.split(':', 1)[0] : '';
      if (name.startsWith('on') || (['href', 'src', 'xlink:href'].includes(name) && protocol === 'javascript')) {
        node.removeAttribute(attribute.name);
      }
    });
  });
  return document.body.innerHTML;
}

const EmptyState = ({ error, onRetry }) => (
  <div className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center">
    <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EEE8F1] text-[#63436F]">
      <BookOpen className="h-7 w-7" />
    </span>
    <h2 className="text-xl font-semibold text-slate-900">{error ? 'Documentation indisponible' : 'Aucune documentation publiée'}</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
      {error || 'Lancez le skill prod-doc pour publier la première documentation de production.'}
    </p>
    {error && (
      <button onClick={onRetry} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#523467] px-4 py-2.5 text-sm font-semibold text-white">
        <RefreshCw className="h-4 w-4" /> Réessayer
      </button>
    )}
  </div>
);

const ProjectRequiredState = ({ onChoose }) => (
  <div className="flex min-h-[calc(100vh-64px)] flex-col items-center justify-center bg-[#F5F6F8] px-6 text-center">
    <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EEE8F1] text-[#63436F]">
      <BookOpen className="h-7 w-7" />
    </span>
    <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Choisissez d’abord un projet</h1>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
      La documentation affichée dépend du projet sélectionné dans votre espace.
    </p>
    <button onClick={onChoose} className="mt-6 rounded-xl bg-[#523467] px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#452954] focus:outline-none focus:ring-2 focus:ring-[#765381] focus:ring-offset-2">
      Choisir un projet
    </button>
  </div>
);

const FunctionalDocument = ({ document, assetUrl, activeSection }) => {
  const groups = document.groups || document.functional_doc?.changes || [];
  if (!groups.length && document.functional_doc?.overview) {
    return <p className="max-w-3xl text-[15px] leading-7 text-slate-700">{document.functional_doc.overview}</p>;
  }

  const pages = groups.flatMap((group, groupIndex) => (group.pages || [group]).map((page, pageIndex) => ({
    group,
    groupIndex,
    page,
    pageIndex,
    id: functionalSectionId(groupIndex, pageIndex),
  })));
  const selected = pages.find(page => page.id === activeSection) || pages[0];
  if (!selected) return null;

  const { group, groupIndex, page, pageIndex, id } = selected;

  return (
    <section key={`${group.id || group.title || 'group'}-${groupIndex}`}>
      <div className="mb-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200" />
        <h2 className="text-sm font-semibold text-[#63436F]">{group.title || group.label || 'Fonctionnalités'}</h2>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <article id={id} key={`${page.id || page.title || 'page'}-${pageIndex}`} className="min-w-0 scroll-mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white px-6 py-6 shadow-[0_8px_30px_rgba(30,20,35,0.04)] sm:px-8">
        <h3 className="text-xl font-semibold tracking-tight text-slate-900">{page.title}</h3>
        {(page.introduction || page.intro || page.description) && (
          <p className="mt-2 text-[15px] leading-7 text-slate-500">{page.introduction || page.intro || page.description}</p>
        )}
        {page.content_html && (
          <div
            className="documentation-content mt-5 border-t border-slate-100 pt-5 text-[15px] leading-7 text-slate-700"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content_html) }}
          />
        )}
        {Array.isArray(page.screenshots) && page.screenshots.length > 0 && (
          <div className="mt-6 grid gap-5 border-t border-slate-100 pt-6">
            {page.screenshots.filter(screenshot => screenshot?.src).map((screenshot, screenshotIndex) => (
              <figure key={`${screenshot.src}-${screenshotIndex}`} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <img
                  src={assetUrl(screenshot.src)}
                  alt={screenshot.alt || screenshot.caption || `Illustration ${screenshotIndex + 1}`}
                  loading="lazy"
                  className="h-auto w-full bg-white object-contain"
                />
                {screenshot.caption && <figcaption className="px-4 py-3 text-sm leading-5 text-slate-500">{screenshot.caption}</figcaption>}
              </figure>
            ))}
          </div>
        )}
      </article>
    </section>
  );
};

const labelForKey = key => ({
  architecture: 'Architecture', modules: 'Modules', models: 'Modèles', views: 'Vues',
  security: 'Sécurité', scheduled_actions: 'Actions planifiées', controllers: 'Contrôleurs',
  reports: 'Rapports', tests: 'Tests', deployment: 'Déploiement', diagnostics: 'Diagnostic',
  rollback: 'Retour arrière', risks: 'Risques', changed_files: 'Fichiers modifiés',
}[key] || key.replaceAll('_', ' '));

const StructuredValue = ({ value }) => {
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <p className="whitespace-pre-wrap text-sm leading-6 text-slate-600">{String(value)}</p>;
  }
  if (Array.isArray(value)) {
    return <div className="space-y-2">{value.map((item, index) => (
      <div key={index} className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3"><StructuredValue value={item} /></div>
    ))}</div>;
  }
  return <dl className="space-y-3">{Object.entries(value).map(([key, item]) => (
    <div key={key} className="grid gap-1 sm:grid-cols-[150px_1fr]">
      <dt className="text-xs font-semibold text-slate-500">{labelForKey(key)}</dt>
      <dd><StructuredValue value={item} /></dd>
    </div>
  ))}</dl>;
};

const hasContent = value => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.some(hasContent);
  if (typeof value === 'object') return Object.values(value).some(hasContent);
  return true;
};

const DocumentationTree = ({ items, expandedGroups, setExpandedGroups, activeSection, onSelect }) => (
  <div className="space-y-1 py-1">
    {items.map(item => item.children ? (
      <div key={item.id}>
        <button
          type="button"
          onClick={() => setExpandedGroups(current => ({ ...current, [item.id]: !current[item.id] }))}
          aria-expanded={Boolean(expandedGroups[item.id])}
          aria-controls={`${item.id}-children`}
          className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
        >
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${expandedGroups[item.id] ? 'rotate-90' : ''}`} />
          <span>{item.title}</span>
        </button>
        {expandedGroups[item.id] && (
          <div id={`${item.id}-children`} className="ml-4 space-y-0.5 border-l border-slate-200 pl-2.5">
            {item.children.map(child => (
              <button
                key={child.id}
                type="button"
                onClick={() => onSelect(child.id)}
                aria-controls={child.id}
                aria-current={activeSection === child.id ? 'page' : undefined}
                className={`w-full rounded-md px-2.5 py-1.5 text-left text-[13px] leading-5 transition-colors ${activeSection === child.id ? 'bg-[#EEE8F1] font-semibold text-[#523467]' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}
              >
                {child.title}
              </button>
            ))}
          </div>
        )}
      </div>
    ) : (
      <button
        key={item.id}
        type="button"
        onClick={() => onSelect(item.id)}
        aria-controls={item.id}
        aria-current={activeSection === item.id ? 'page' : undefined}
        className={`w-full rounded-lg px-2.5 py-2 text-left text-sm leading-5 transition-colors ${activeSection === item.id ? 'bg-[#EEE8F1] font-semibold text-[#523467]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
      >
        {item.title}
      </button>
    ))}
  </div>
);

const TechnicalDocument = ({ document }) => {
  const source = document.technical || document.technical_doc || document;
  const keys = ['overview', 'changes', 'architecture', 'modules', 'models', 'views', 'security', 'scheduled_actions', 'controllers', 'reports', 'tests', 'deployment', 'diagnostics', 'rollback', 'risks', 'changed_files'];
  return (
    <div className="space-y-5">
      {keys.map(key => {
        const value = source[key];
        if (!hasContent(value)) return null;
        return (
          <section id={technicalSectionId(key)} key={key} className="min-w-0 scroll-mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white px-6 py-6 shadow-[0_8px_30px_rgba(30,20,35,0.04)] sm:px-8">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900"><Code2 className="h-5 w-5 text-[#765381]" />{labelForKey(key)}</h2>
            <StructuredValue value={value} />
          </section>
        );
      })}
      {Array.isArray(source.commits) && source.commits.length > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white px-6 py-6 sm:px-8">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900"><GitCommitHorizontal className="h-5 w-5 text-[#765381]" />Commits documentés</h2>
          <div className="divide-y divide-slate-100">
            {source.commits.map((commit, index) => (
              <div key={commit.hash || index} className="grid gap-1 py-3 sm:grid-cols-[90px_1fr_auto] sm:items-center">
                <code className="text-xs font-semibold text-[#765381]">{commit.short_hash || commit.hash?.slice(0, 7)}</code>
                <span className="text-sm text-slate-700">{commit.subject || commit.message}</span>
                <span className="text-xs text-slate-400">{commit.author}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const DocumentationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInternalUser } = useAuth();
  const selectedOdooProjectId = typeof localStorage !== 'undefined'
    ? Number(localStorage.getItem('selected_project_id')) || null
    : null;
  const [catalog, setCatalog] = useState({ projects: [], canViewTechnical: false });
  const [selectedId, setSelectedId] = useState(null);
  const [kind, setKind] = useState('functional');
  const [version, setVersion] = useState('latest');
  const [document, setDocument] = useState(null);
  const [search, setSearch] = useState('');
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [pendingSourceAnchor, setPendingSourceAnchor] = useState(null);

  const loadCatalog = useCallback(async () => {
    if (!isInternalUser && !selectedOdooProjectId) {
      setCatalog({ projects: [], canViewTechnical: false });
      setSelectedId(null);
      setLoadingCatalog(false);
      return;
    }
    setLoadingCatalog(true); setError(null);
    try {
      const data = await backendApi.getDocumentationProjects(isInternalUser ? null : selectedOdooProjectId);
      setCatalog(data);
      setSelectedId(current => {
        if (current && data.projects.some(project => (project.catalogId || project.id) === current)) return current;
        return data.projects[0] ? (data.projects[0].catalogId || data.projects[0].id) : null;
      });
    } catch (loadError) { setError(loadError.message); }
    finally { setLoadingCatalog(false); }
  }, [isInternalUser, selectedOdooProjectId]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  const selectedProject = catalog.projects.find(project => (project.catalogId || project.id) === selectedId);
  useEffect(() => {
    if (!selectedProject) { setDocument(null); return; }
    let active = true;
    setLoadingDocument(true); setError(null);
    backendApi.getDocumentation(selectedProject.id, {
      projectId: selectedProject.odooProjectId || selectedOdooProjectId,
      version,
      kind,
    })
      .then(data => { if (active) setDocument(data.document); })
      .catch(loadError => { if (active) { setDocument(null); setError(loadError.message); } })
      .finally(() => { if (active) setLoadingDocument(false); });
    return () => { active = false; };
  }, [selectedProject, selectedOdooProjectId, version, kind]);

  const documentationAssetUrl = useCallback(assetPath => backendApi.getDocumentationAssetUrl(
    selectedProject?.id,
    assetPath,
    selectedProject?.odooProjectId || selectedOdooProjectId,
  ), [selectedProject, selectedOdooProjectId]);
  const visibleProjects = useMemo(() => catalog.projects.filter(project =>
    normalize(project.name).includes(normalize(search))), [catalog.projects, search]);
  const tableOfContents = useMemo(() => {
    if (!document) return [];
    if (kind === 'technical') {
      const source = document.technical || document.technical_doc || document;
      return ['overview', 'changes', 'architecture', 'modules', 'models', 'views', 'security', 'scheduled_actions', 'controllers', 'reports', 'tests', 'deployment', 'diagnostics', 'rollback', 'risks', 'changed_files']
        .filter(key => hasContent(source[key]))
        .map(key => ({ id: technicalSectionId(key), title: labelForKey(key) }));
    }
    const groups = document.groups || document.functional_doc?.changes || [];
    return groups.map((group, groupIndex) => ({
      id: `documentation-functional-group-${groupIndex}`,
      title: group.title || group.label || `Groupe ${groupIndex + 1}`,
      children: (group.pages || [group]).map((page, pageIndex) => ({
        id: functionalSectionId(groupIndex, pageIndex),
        title: page.title || group.title || group.label || `Section ${pageIndex + 1}`,
      })),
    }));
  }, [document, kind]);

  useEffect(() => {
    const firstItem = tableOfContents[0];
    const firstSection = firstItem?.children?.[0]?.id || firstItem?.id || null;
    setActiveSection(firstSection);
    setExpandedGroups(firstItem?.children ? { [firstItem.id]: true } : {});
  }, [tableOfContents]);

  const selectProject = catalogId => {
    const project = catalog.projects.find(item => (item.catalogId || item.id) === catalogId);
    setSelectedId(catalogId);
    setVersion(project?.hasFunctional ? 'latest' : (project?.versions[0]?.label || 'latest'));
    setKind('functional');
  };
  const navigateToSection = sectionId => {
    setActiveSection(sectionId);
  };
  const navigateFromAssistant = source => {
    if (source.mode && source.mode !== kind) setKind(source.mode);
    setPendingSourceAnchor(source.anchor);
  };
  useEffect(() => {
    if (!pendingSourceAnchor || loadingDocument) return;
    const groupIndex = pendingSourceAnchor.match(/^documentation-functional-(\d+)-/)?.[1];
    if (groupIndex !== undefined) {
      setExpandedGroups(current => ({ ...current, [`documentation-functional-group-${groupIndex}`]: true }));
    }
    setActiveSection(pendingSourceAnchor);
    setPendingSourceAnchor(null);
  }, [pendingSourceAnchor, loadingDocument, document]);
  useEffect(() => {
    if (!activeSection) return;
    const frame = window.requestAnimationFrame(() => {
      window.document.getElementById(activeSection)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeSection]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const source = location.state?.dynamicsSource || (params.get('section') ? {anchor:params.get('section'),documentationId:params.get('document'),odooProjectId:selectedOdooProjectId,selectedVersion:params.get('version'),mode:params.get('mode')} : null);
    if (!source || loadingCatalog) return;
    const project = catalog.projects.find(p => p.id === source.documentationId && p.odooProjectId === source.odooProjectId);
    if (project) {
      setSelectedId(project.catalogId || project.id);
      setVersion(source.selectedVersion || 'latest');
      setKind(source.mode === 'technical' && isInternalUser ? 'technical' : 'functional');
      setPendingSourceAnchor(source.anchor);
    }
    navigate(location.pathname, {replace:true,state:null});
  }, [location, loadingCatalog, catalog.projects, isInternalUser, navigate, selectedOdooProjectId]);

  useDynamicsPage({path:'/dashboard/documentation',title:'Documentation',kind:'documentation', projectId:selectedProject?.odooProjectId,projectName:selectedProject?.name,documentationId:selectedProject?.id,version,mode:kind,anchor:activeSection,filters:{search},ready:!loadingCatalog && !loadingDocument});
  useEffect(() => {
    const handler = event => navigateFromAssistant(event.detail);
    window.addEventListener('dynamics:source', handler);
    return () => window.removeEventListener('dynamics:source', handler);
  });

  if (!loadingCatalog && !isInternalUser && !selectedOdooProjectId) return <ProjectRequiredState onChoose={() => navigate('/')} />;
  if (!loadingCatalog && (!catalog.projects.length || (error && !selectedId))) return <EmptyState error={error} onRetry={loadCatalog} />;

  return (
    <div className="flex min-h-full w-full bg-[#F5F6F8]">
      <div className="min-w-0 flex-1 overflow-x-hidden">
      <div className="border-b border-slate-200 bg-white px-5 py-7 sm:px-8">
        <div className="flex w-full items-end justify-between gap-6">
          <div><h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">Documentation</h1><p className="mt-1 text-sm text-slate-500">Guides et références issus des versions en production.</p></div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 text-xs text-slate-400 xl:flex"><ShieldCheck className="h-4 w-4" />Accès adapté à votre profil</div>
            {DYNAMICS_ENABLED && (
              <button type="button" onClick={openDynamics} aria-label="Ouvrir l’assistant documentaire" className="inline-flex items-center gap-2 rounded-xl bg-[#523467] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#452954] focus:outline-none focus:ring-2 focus:ring-[#765381] focus:ring-offset-2">
                <DynamicsIcon className="h-4 w-4" /><span className="hidden sm:inline">Dynamix</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid w-full gap-0 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 bg-white p-4 lg:sticky lg:top-0 lg:h-screen lg:self-start lg:overflow-y-auto lg:border-b-0 lg:border-r">
          {catalog.projects.length > 1 && (
            <label className="relative block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Rechercher un projet" className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#765381] focus:ring-2 focus:ring-[#765381]/10" />
            </label>
          )}
          <p className={`${catalog.projects.length > 1 ? 'mt-5' : ''} px-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400`}>Projet</p>
          <div className="mt-2 space-y-1">
            {visibleProjects.map(project => {
              const projectKey = project.catalogId || project.id;
              const selected = selectedId === projectKey;
              const treeId = `documentation-tree-${normalize(projectKey)}`;
              return (
                <React.Fragment key={projectKey}>
                  <button
                    type="button"
                    onClick={() => selectProject(projectKey)}
                    aria-expanded={selected && tableOfContents.length > 0}
                    aria-controls={selected && tableOfContents.length > 0 ? treeId : undefined}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left ${selected ? 'bg-[#EEE8F1] text-[#523467]' : 'text-slate-600 hover:bg-slate-50'}`}
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{project.name}</span><span className="mt-0.5 block text-xs opacity-60">{project.latestVersion || 'Version initiale'}</span></span>
                    <ChevronRight className={`h-4 w-4 shrink-0 opacity-50 transition-transform ${selected && tableOfContents.length > 0 ? 'rotate-90' : ''}`} />
                  </button>
                  {selected && tableOfContents.length > 0 && (
                    <nav id={treeId} className="ml-5 border-l-2 border-[#E7DCEB] pl-2" aria-label="Sommaire de la documentation">
                      <p className="flex items-center gap-1.5 px-2.5 pb-1 pt-2 text-[11px] font-medium text-slate-400"><ListTree className="h-3.5 w-3.5" />Pages</p>
                      <DocumentationTree
                        items={tableOfContents}
                        expandedGroups={expandedGroups}
                        setExpandedGroups={setExpandedGroups}
                        activeSection={activeSection}
                        onSelect={navigateToSection}
                      />
                    </nav>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </aside>

        <main className="min-w-0 px-5 py-7 sm:px-8 lg:px-10 xl:px-12">
          {selectedProject && (
            <div className="w-full min-w-0">
              <div className="flex flex-col justify-between gap-5 border-b border-slate-200 pb-6 sm:flex-row sm:items-end">
                <div><p className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#765381]"><FileClock className="h-4 w-4" />Branche {selectedProject.productionRef || 'de production'}</p><h2 className="text-2xl font-semibold tracking-tight text-slate-950">{selectedProject.name}</h2><p className="mt-2 flex items-center gap-2 text-sm text-slate-500"><CalendarDays className="h-4 w-4" />Mise à jour le {formatDate(document?.date || selectedProject.latestDate)}</p></div>
                <select aria-label="Version de la documentation" value={version} onChange={event => setVersion(event.target.value)} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 outline-none focus:border-[#765381]">
                  <option value="latest">Version actuelle</option>
                  {selectedProject.versions.map((item, index) => <option key={`${item.label}-${index}`} value={item.label}>{item.version} · {formatDate(item.date)}{item.type === 'delta' ? ' · Mise à jour' : ''}</option>)}
                </select>
              </div>

              <div className="my-6 flex w-fit rounded-xl bg-slate-200/70 p-1" role="tablist">
                <button role="tab" aria-selected={kind === 'functional'} onClick={() => setKind('functional')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${kind === 'functional' ? 'bg-white text-[#523467] shadow-sm' : 'text-slate-500'}`}>Guide fonctionnel</button>
                {catalog.canViewTechnical && <button role="tab" aria-selected={kind === 'technical'} onClick={() => setKind('technical')} className={`rounded-lg px-4 py-2 text-sm font-semibold ${kind === 'technical' ? 'bg-white text-[#523467] shadow-sm' : 'text-slate-500'}`}>Référence technique</button>}
              </div>

              {loadingDocument ? <div className="flex min-h-[360px] items-center justify-center"><RefreshCw className="h-6 w-6 animate-spin text-[#765381]" /></div>
                : error ? <EmptyState error={error} onRetry={() => { setVersion('latest'); setKind('functional'); }} />
                : document ? (kind === 'technical' ? <TechnicalDocument document={document} /> : <FunctionalDocument document={document} assetUrl={documentationAssetUrl} activeSection={activeSection} />) : null}
            </div>
          )}
        </main>
      </div>
      </div>

    </div>
  );
};

export default DocumentationPage;
